import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { Subscription } from 'rxjs';

import { DecimalPlacesStandalonePipe } from 'v2/modules/shared/pipes/decimal-places.pipe';
import { LumCurrencyStandalonePipe } from 'v2/modules/shared/pipes/lum-currency.pipe';
import { trackByControlValue } from 'v2/modules/shared/util/track-by.util';
import { UiEmptyPageStandaloneComponent } from 'v2/modules/ui/components/ui-empty-page/ui-empty-page.component';

import { ExtraCostFormGroup } from './extra-cost.form-group';
import { IExtraCost } from './extra-cost.types';

const _UNSUBSCRIBE_ALL = (sub :Subscription) => sub.unsubscribe();

@Component({
  selector: 'lum-extra-costs',
  standalone: true,
  imports: [
    CommonModule,
    DecimalPlacesStandalonePipe,
    LumCurrencyStandalonePipe,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    ReactiveFormsModule,
    UiEmptyPageStandaloneComponent,
  ],
  templateUrl: './extra-costs.component.html',
  styleUrls: ['./extra-costs.component.scss'],
})
export class ExtraCostsComponent implements OnChanges, OnDestroy, OnInit {
  @Input() extraCosts ?:IExtraCost[] = [];
  @Input() form ?:FormGroup | UntypedFormGroup;
  @Input() formExtraCostsFieldName ?:string = 'extraCosts';
  @Input() mode :'create' | 'read' | 'update' = 'read';

  extraCostsForm :FormArray<ExtraCostFormGroup>;
  total :number = 0;
  totalQuantity :number = 0;
  trackByControlValue = trackByControlValue;

  #subscriptions = new Map<ExtraCostFormGroup, [Subscription, Subscription]>();

  constructor() {}

  ngOnInit() :void {
    const groups = this.extraCosts.map((extraCost :IExtraCost) =>
      this.#buildFormGroup(extraCost),
    );

    this.extraCostsForm = new FormArray<ExtraCostFormGroup>(groups);

    this.#updateTotalQuantity();
    this.#updateTotal();

    if (this.form) {
      this.form.setControl(this.formExtraCostsFieldName, this.extraCostsForm);
    }
  }

  ngOnChanges(changes :SimpleChanges) :void {
    if (changes['extraCosts'] && !changes['extraCosts'].firstChange) {
      this.extraCosts.forEach((extraCost :IExtraCost) => {
        this.extraCostsForm.push(this.#buildFormGroup(extraCost));
      });

      this.#updateTotalQuantity();
      this.#updateTotal();
    }
  }

  ngOnDestroy() :void {
    this.#subscriptions.forEach((subs) => subs.forEach(_UNSUBSCRIBE_ALL));
    this.#subscriptions.clear();
  }

  handleAddExtraCostRows(e :Event, rows :number) {
    e.preventDefault();

    this.#extraCostRowAdd(rows);
  }
  handleRemoveExtraCostRow(e :Event, rowIndex :number) {
    e.preventDefault();

    this.#extraCostRowRemove(rowIndex);
  }

  #extraCostRowAdd(count :number) :void {
    while (count > 0) {
      count--;
      this.extraCostsForm.push(this.#buildFormGroup());
    }

    this.#updateTotalQuantity();
    this.#updateTotal();
  }
  #extraCostRowRemove(index :number) :void {
    const group = this.extraCostsForm.at(index);

    this.#unsubscribeTo(group);

    this.extraCostsForm.removeAt(index);

    this.#updateTotalQuantity();
    this.#updateTotal();
  }

  #subscribeTo(group :ExtraCostFormGroup) {
    this.#unsubscribeTo(group);
    this.#subscriptions.set(group, [
      group.controls.lineTotal.valueChanges.subscribe(() => {
        this.#updateTotal();
      }),
      group.controls.quantity.valueChanges.subscribe(() => {
        this.#updateTotalQuantity();
      }),
    ]);
  }
  #unsubscribeTo(group :ExtraCostFormGroup) {
    if (this.#subscriptions.has(group)) {
      this.#subscriptions.get(group).forEach(_UNSUBSCRIBE_ALL);
      this.#subscriptions.delete(group);
    }
  }

  #buildFormGroup(extraCost ?:IExtraCost) {
    const group = extraCost
      ? ExtraCostFormGroup.fromData(extraCost)
      : new ExtraCostFormGroup({
          name: new FormControl<string>(''),
          quantity: new FormControl<number>(0),
          unitPrice: new FormControl<number>(0),
        });

    this.#subscribeTo(group);

    return group;
  }

  #updateTotal() {
    this.total = this.extraCostsForm.controls.reduce(
      (x :number, group :ExtraCostFormGroup) => x + group.controls.lineTotal.value,
      0,
    );
  }
  #updateTotalQuantity() {
    this.totalQuantity = this.extraCostsForm.controls.reduce(
      (x :number, group :ExtraCostFormGroup) => x + group.controls.quantity.value,
      0,
    );
  }
}
